import { BUBBLE_MAX, CHAT_MAX, type ChatMessage } from '@tanky/shared';
import { api } from '../api';
import { h, toast } from '../dom';
import { session } from '../session';

export interface ChatPanel {
  el: HTMLElement;
  add(msg: ChatMessage): void;
  setMessages(msgs: ChatMessage[]): void;
  /** Funkce, která vrátí barvu hráče (barva tanku). */
  colorOf: (userId: string) => string;
  focus(): void;
}

/** Chat hry s přepínačem „Bublina“ (krátká zpráva nad tankem). */
export function chatPanel(gameId: string, opts: { canWrite: () => boolean; allowBubble: () => boolean }): ChatPanel {
  const list = h('div', { class: 'chat-list' });
  const input = h('input', { class: 'chat-input', maxlength: CHAT_MAX, placeholder: 'Napiš zprávu…', autocomplete: 'off', enterkeyhint: 'send' }) as HTMLInputElement;
  const bubble = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const bubbleLabel = h('label', { class: 'bubble-toggle', title: `Zobrazí se na pár sekund v bublině u tvého tanku (max. ${BUBBLE_MAX} znaků)` }, bubble, h('span', {}, '💭 Bublina'));
  const counter = h('span', { class: 'chat-counter muted small' });
  const sendBtn = h('button', { type: 'submit', class: 'primary' }, '➤');
  let messages: ChatMessage[] = [];

  function updateLimits() {
    const max = bubble.checked ? BUBBLE_MAX : CHAT_MAX;
    input.maxLength = max;
    if ([...input.value].length > max) input.value = [...input.value].slice(0, max).join('');
    const len = [...input.value].length;
    counter.textContent = bubble.checked || len > CHAT_MAX - 30 ? `${len}/${max}` : '';
    input.placeholder = bubble.checked ? 'Krátký vzkaz do bubliny…' : 'Napiš zprávu…';
    bubbleLabel.style.display = opts.allowBubble() ? '' : 'none';
    if (!opts.allowBubble()) bubble.checked = false;
  }
  bubble.addEventListener('change', () => {
    updateLimits();
    input.focus();
  });
  input.addEventListener('input', updateLimits);

  function row(m: ChatMessage) {
    const mine = m.userId === session.me?.id;
    return h(
      'div',
      { class: `chat-msg${mine ? ' mine' : ''}` },
      h('b', { style: { color: panel.colorOf(m.userId) } }, m.name),
      m.bubble ? h('span', { class: 'chat-bubble-tag', title: 'bublina' }, '💭') : null,
      h('span', {}, m.text),
    );
  }

  function render() {
    list.replaceChildren(
      ...(messages.length ? messages.map(row) : [h('div', { class: 'muted small' }, 'Zatím ticho. Napiš něco!')]),
    );
    list.scrollTop = list.scrollHeight;
  }

  const form = h(
    'form',
    {
      class: 'chat-form',
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const text = input.value.trim();
        if (!text || !opts.canWrite()) return;
        sendBtn.disabled = true;
        try {
          await api.chat(gameId, text, bubble.checked);
          input.value = '';
          updateLimits();
        } catch (err) {
          toast((err as Error).message, true);
        } finally {
          sendBtn.disabled = false;
        }
      },
    },
    h('div', { class: 'row' }, h('div', { class: 'grow' }, input), sendBtn),
    h('div', { class: 'row chat-opts' }, bubbleLabel, counter),
  );

  const el = h('div', { class: 'chat' }, list, form);

  const panel: ChatPanel = {
    el,
    colorOf: () => 'var(--accent)',
    add(msg) {
      if (messages.some((m) => m.id === msg.id)) return;
      messages.push(msg);
      if (messages.length > 100) messages = messages.slice(-100);
      render();
    },
    setMessages(msgs) {
      messages = msgs.slice(-100);
      render();
    },
    focus() {
      updateLimits();
      form.style.display = opts.canWrite() ? '' : 'none';
      render();
      if (opts.canWrite() && matchMedia('(pointer: fine)').matches) input.focus();
    },
  };
  updateLimits();
  render();
  return panel;
}
